import React, { useState, useEffect } from 'react';
import { List } from '@mui/material';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import ReportListItem from './ReportListItem';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { get } from 'global/lib/lodash';
import ReportFab from 'feature/report/ReportFab';
import { useScreen } from 'global/styles';
import { MarginBot, RlRow } from './ReportList.styles';
import { Text } from 'global/components/text';
import ReportsFilters from 'feature/report/Filters';
import withSearchBar from 'global/components/withSearchBar';
import { FilterChips } from 'global/components/filterchips';
import moment from 'moment';
import { getNativeReportsVisible, isNativeShell } from 'global/utils/nativeShell';
import { mapSearchAssessments } from 'global/utils/mapSearchAssessment';

const AssessmentIncludes = ['realm', 'realm.parent', 'authors', 'editors', 'lockedBy'];
const pageSize = 25;
const hasSearchQuery = (filter) => !!(String(get(filter, 'searchstring', '') || '').trim());

const listFilterProps = (filter) =>
  buildFilters(filter, {
    includeRealm: true,
    convertName: {
      realm_type: 'tier',
    },
    // `realms` is Data-tab only; Browse uses includeRealm → `realm`. Strip if persisted state is polluted.
    dontFilter: ['quality_type', 'superset_id', 'user_id', 'edition_id', 'status', 'realms', 'ownership', 'searchstring'],
    dontSort: hasSearchQuery(filter),
    overrideFilter: (filt) => {
      delete filt.ownership;
      if (filt.scorecard_id === 0) {
        delete filt.scorecard_id;
      }
      if (filt.vendor_id === 0) {
        delete filt.vendor_id;
      }
      if (filt.template_id === 0) {
        delete filt.template_id;
      }
      if (filt.start_date) {
        if (!moment(filt.start_date).isValid()) {
          delete filt.start_date;
        }
      }
      if (filt.end_date) {
        if (!moment(filt.end_date).isValid()) {
          delete filt.end_date;
        }
      }
      return filt;
    },
  });

const buildRetrieve = (filter, pageNumber = 1) => {
  const searching = hasSearchQuery(filter);
  const built = listFilterProps(filter);
  const page = { size: pageSize, number: pageNumber };
  // Search API takes `q` as a top-level query param, first, not filter[name]/realm_name.
  const params = searching
    ? { q: String(get(filter, 'searchstring', '') || '').trim(), page, ...built }
    : { page, include: AssessmentIncludes.join(','), ...built };

  return {
    url: searching ? '/search/assessments' : '/assessments',
    opts: {
      build: ['assessments'],
      useEffect: true,
      useNamespace: !searching,
      returnRaw: searching,
      transformData: searching ? mapSearchAssessments : (d) => d,
      params,
    },
  };
};

export default function ReportList(props) {
  const filter = useSelector((state) => get(state, 'filter.reports.list'));
  const realm = useSelector((state) => get(state, 'auth.realm.id'));
  const [retrieve, setRetrieve] = useState(() => buildRetrieve(filter));

  useEffect(() => {
    if (isNativeShell() && getNativeReportsVisible()) return;
    setRetrieve(buildRetrieve(filter));
  }, [filter, realm]);

  const { data: assessments, meta, loading, error } = useRetrieveData({ url: retrieve.url }, retrieve.opts);

  const lastPage = get(meta, 'page.last-page', null);
  const updatePage = (newPage) => {
    if (newPage <= lastPage) {
      setRetrieve(buildRetrieve(filter, newPage));
    }
  };

  return (
    <>
      <Header {...props} includeFab />
      <ListWithPages
        handlePageChange={updatePage}
        currentPage={get(retrieve, 'opts.params.page.number', 1)}
        lastPage={lastPage}
        data={assessments}
        loading={loading}
        error={error}
        {...props}
        hideSortWhenSearching
      />
    </>
  );
}

const ListWithPages = withSearchBar(withInfiniteScroll(ReportListItem, List));

export const Header = (props) => {
  const { includeFab } = props;

  const { isMobile } = useScreen();

  const padLeft = get(props, 'padLeft', true);

  const title = get(props, 'title', 'Assessments');

  return (
    <RlRow padLeft={padLeft}>
      <div>
        {!isMobile && <MarginBot as={Text} type={'headline_medium'}>
          {title}
        </MarginBot>}
        {/* Data shows realm via the `realms` Droprealms chips; list/photo need the global picker chip. */}
        {isMobile ? <ReportsFilters {...props} /> : <FilterChips id={`reports.${props.display || 'list'}`} includeRealm={props.display !== 'data'} />}
        {props.children || null}
      </div>
      {includeFab && <ReportFab />}
    </RlRow>
  );
};
